import React, { useMemo, useState } from 'react';
import styles from './SeatedAvailabilityBar.module.scss';

type StatusKey =
  | 'free'
  | 'available'
  | 'booked'
  | 'reservedByToken'
  | 'not_for_sale'
  | 'pending'
  | (string & {}); // allow any additional keys gracefully

export interface AvailabilityData {
  total: number;
  availability_by_reason: Record<StatusKey, number>;
  translate: Record<string, string>;
}

interface SegmentedBarProps {
  data: AvailabilityData;
  /** CSS width for the whole bar container (e.g. "320px" or "100%"). Default: "320px" */
  width?: string;
  /** CSS height for the bar. Default: 12px */
  height?: number;
  /** Optional className to wrap the whole widget */
  className?: string;
}

/** Hard-coded color mapping for known statuses (fallback provided for unknowns) */
const STATUS_COLORS: Record<StatusKey, string> = {
  free: '#d1d5db', // gray-300 (light gray for available/free)
  available: '#d1d5db', // gray-300 (light gray for available/free)
  booked: '#16a34a', // green-600 (booked is a good thing)
  reservedByToken: '#f59e0b', // amber-500
  not_for_sale: '#6b7280', // gray-500
  pending: '#3b82f6', // blue-500
};

const FALLBACK_COLOR = '#a855f7'; // violet-500 for unknown statuses
export const SeatsAvailabilityBar: React.FC<SegmentedBarProps> = ({
  data,
  width = '480px',
  height = 16,
  className,
}) => {
  const [hovered, setHovered] = useState<StatusKey | null>(null);

  const { total, availability_by_reason: byReason, translate } = data;

  // Derive a consistent display order where free/available are always last.
  const orderedStatuses: StatusKey[] = useMemo(() => {
    const allKeys = Object.keys(byReason) as StatusKey[];

    // Known non-free statuses we want first, in this specific order if present
    const headKnown: StatusKey[] = ['booked', 'reservedByToken', 'not_for_sale', 'pending'];
    const head = headKnown.filter(k => allKeys.includes(k));

    // Any other unknown/extra statuses (excluding free/available), sorted alphabetically
    const exclude: StatusKey[] = [...headKnown, 'free', 'available'];
    const extras = allKeys.filter(k => !exclude.includes(k)).sort();

    // Finally, free/available at the very end, but only if present
    const tail = (['free', 'available'] as StatusKey[]).filter(k => allKeys.includes(k));

    return [...head, ...extras, ...tail];
  }, [byReason]);

  const segments = useMemo(() => {
    const valuesSum = Object.values(byReason || {}).reduce((acc, v) => acc + v, 0);
    // Prefer provided total when valid; otherwise fall back to the sum of values
    const denom = total && total > 0 ? total : valuesSum;

    return orderedStatuses.map(status => {
      const value = byReason[status] || 0;
      const pct = denom > 0 ? (value / denom) * 100 : 0;
      const color = STATUS_COLORS[status] || FALLBACK_COLOR;
      const label = translate[status] || status;
      return { status, value, pct, color, label };
    });
  }, [orderedStatuses, byReason, total, translate]);

  // Decide if we have any values to render
  const hasAnyValue = useMemo(() => segments.some(s => s.value > 0), [segments]);

  return (
    <div className={`${styles.container} ${className || ''}`}>
      {/* Header: top-right capacity */}
      <div className={styles.header} aria-hidden>
        <span className={styles.headerText}>
          {(translate && translate['capacity']) || 'Capacity'}: {total}
        </span>
      </div>

      {/* Bar */}
      <div
        role="img"
        aria-label="Seat availability segmented bar"
        className={styles.bar}
        style={{ width, height, borderRadius: 4 }}
      >
        {!hasAnyValue ? (
          <div className={styles.segment} style={{ width: '100%' }} />
        ) : (
          segments.map(seg => {
            const dimOthers = hovered && hovered !== seg.status;
            const classNames = [
              styles.segment,
              dimOthers ? styles.segmentDimmed : '',
              hovered === seg.status ? styles.segmentFocused : '',
            ]
              .filter(Boolean)
              .join(' ');

            const style: React.CSSProperties = {
              width: `${Math.max(seg.pct, 0)}%`,
              background: seg.color,
              visibility: seg.pct <= 0 ? 'hidden' : undefined,
            };

            return (
              <div
                key={seg.status}
                title={`${seg.label}: ${seg.value} (${Math.round(seg.pct)}%)`}
                className={classNames}
                style={style}
                onMouseEnter={() => setHovered(seg.status)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(seg.status)}
                onBlur={() => setHovered(null)}
                tabIndex={0}
                aria-label={`${seg.label}: ${seg.value}`}
              />
            );
          })
        )}
      </div>

      {/* Legend */}
      <div className={styles.legend}>
        {segments
          .filter(seg => seg.value > 0)
          .map(seg => {
            const isActive = hovered === seg.status;
            return (
              <button
                key={seg.status}
                onMouseEnter={() => setHovered(seg.status)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(seg.status)}
                onBlur={() => setHovered(null)}
                onClick={() => setHovered(prev => (prev === seg.status ? null : seg.status))}
                className={`${styles.legendItem} ${isActive ? styles.legendItemActive : ''}`}
                aria-pressed={isActive}
              >
                <span
                  aria-hidden
                  className={styles.legendSwatch}
                  style={{ background: seg.color }}
                />
                <span className={styles.legendLabel}>{seg.label}</span>
                <span className={styles.legendValue}>{seg.value}</span>
              </button>
            );
          })}
      </div>
    </div>
  );
};
